import { VISIT_SOURCE_MAX_PER_VISIT } from "@rosc/contracts";
import { Button as V32Button } from "@rosc/ui/v32/button";
import {
  FileThumb,
  type FileThumbKind,
  type FileThumbState,
  FileTray,
} from "@rosc/ui/v32/file-thumb";
import { Sheet } from "@rosc/ui/v32/sheet";
import {
  Activity,
  Camera,
  FileText,
  FlaskConical,
  Loader2,
  Paperclip,
  RotateCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import type { JSX, ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useSourceNames, useSourceNamesStore } from "@/store/sourceNames.store";
import {
  checkSourceFile,
  formatBytes,
  type PreparedSource,
  prepareSourceFile,
  SOURCE_ACCEPT,
  SOURCE_ACCEPT_FAST,
  type SourceFileError,
  type SourceFileKind,
  SourceFileProblem,
  sourceKindOf,
} from "./prepareSourceFile";
import {
  pdfPageCount,
  type SourcePreview,
  useSourcePreviews,
} from "./sourcePreviews";
import type { VisitSource } from "./useVisitSources";

type Timing = VisitSource["timing"];

// Every upload is labelled a prior record. The API still takes "prior" or
// "today" (older clients send either), but the choice was an extra step that
// rarely mattered (Kevin, 2026-10-25). "prior" is the safe label: the model
// is told never to present a prior record as a finding of today's visit and
// to cite it with its date, so a document is never written up as something
// examined today. Wrongly calling a document "today" could do exactly that.
export const UPLOAD_TIMING: Timing = "prior";

export type SourceUpload = {
  body: {
    timing: Timing;
    mediaType: PreparedSource["mediaType"];
    data: string;
    checksumSha256: string;
    identifiersAcknowledged: true;
  };
  key: string;
};

export interface Props {
  sources: VisitSource[];
  loading?: boolean;
  failed?: boolean;
  onRetryList?: () => void;
  onUpload: (input: SourceUpload) => Promise<unknown>;
  onRemove: (id: number) => Promise<unknown>;
  isIncluded: (id: number) => boolean;
  onIncludedChange: (id: number, included: boolean) => void;
  // Test seam: turns a picked file into the upload body.
  prepare?: (
    file: File,
    options?: { docx?: boolean },
  ) => Promise<PreparedSource>;
  // Fast attach (VITE_ASSISTANT_FAST_UPLOADS_ENABLED): several files at a
  // time, Word documents, no reading at upload. Defaults to the flag.
  fast?: boolean;
  // A picked file became a source: its name, for the page's own labels
  // (never sent).
  onFileNamed?: (id: number, name: string) => void;
  // Pre-chart drafts: files whose add was undone (marked in the tray), and
  // the buttons that add a file, in its preview (Spread into sections,
  // Summarize and insert); `close` closes the preview.
  undoneIds?: ReadonlySet<number>;
  fileActions?: (source: VisitSource, close: () => void) => ReactNode;
}

// Fast attach: files sent at the same time. Each is a single request; two
// at a time keeps a phone's upload link from stalling every chip at once.
const PARALLEL_UPLOADS = 2;

// Kevin's guidance: warn before the first upload of each browser session.
const ACK_KEY = "rosc.assistantUpload.identifiersAcknowledged";
const readAck = (): boolean => {
  try {
    return sessionStorage.getItem(ACK_KEY) === "1";
  } catch {
    return false;
  }
};
const writeAck = (): void => {
  try {
    sessionStorage.setItem(ACK_KEY, "1");
  } catch {
    // Private mode: the warning shows again next time, which is fine.
  }
};

const KIND_ICON = {
  labs: FlaskConical,
  investigation: Activity,
  "previous-note": FileText,
  other: FileText,
} as const;

const formatDate = (value: string, language: string): string => {
  try {
    return new Intl.DateTimeFormat(language, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${value}T00:00:00Z`));
  } catch {
    return value;
  }
};

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    !!target.closest('[contenteditable]:not([contenteditable="false"])') ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

// A pasted image has no useful name ("image.png"): it gets the time it was
// pasted, "Pasted image 14:32.png", numbered when several come at once.
const IMAGE_EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
};
export const pastedImageName = (type: string, at: Date, index = 0): string => {
  const time = `${String(at.getHours()).padStart(2, "0")}:${String(
    at.getMinutes(),
  ).padStart(2, "0")}`;
  const extension = IMAGE_EXTENSION[type] ?? type.split("/")[1] ?? "png";
  return `Pasted image ${time}${index > 0 ? ` (${index + 1})` : ""}.${extension}`;
};
export const pastedImages = (files: readonly File[], at: Date): File[] =>
  files.map(
    (file, index) =>
      new File([file], pastedImageName(file.type, at, index), {
        type: file.type,
        lastModified: at.getTime(),
      }),
  );

// A picked file on its way to becoming a source. Without fast attach there
// is at most one, "reading" while the model reads it. With fast attach it is
// "queued", then "preparing" (re-encoding a photo, hashing), then
// "uploading"; it leaves the list once the server has it.
type Phase = "queued" | "preparing" | "uploading" | "reading" | "error";
type Pending = {
  file: File;
  preview: string | null;
  // PDFs: the page count, once read.
  pages?: number | null;
  key: string;
  phase: Phase;
  error?: string;
  // X pressed while the upload was in flight: removed once it lands.
  cancelled?: boolean;
  // Refused before upload (type, size): trying again cannot help.
  refused?: boolean;
};
const ACTIVE: ReadonlySet<Phase> = new Set<Phase>([
  "preparing",
  "uploading",
  "reading",
]);

type Context = Omit<
  Props,
  "prepare" | "onUpload" | "onRemove" | "onFileNamed"
> & {
  // Removes a source after the clinician confirmed it (the preview sheet).
  removeNow: (source: VisitSource) => void;
  fast: boolean;
  busy: boolean;
  dragging: boolean;
  pending: Pending[];
  // File names picked in this session, by source id (never sent: a file
  // name can identify the patient).
  names: ReadonlyMap<number, string>;
  pickError: string | null;
  removeError: boolean;
  pickFile: () => void;
  takePhoto: () => void;
  retry: (key: string) => void;
  dismissPending: (key: string) => void;
  askRemove: (source: VisitSource) => void;
};
const SourcesContext = createContext<Context | null>(null);

// One short line in place of the old permanent box; the first upload of a
// session still needs the full warning and an explicit confirmation.
export const IdentifierNotice = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs leading-5 text-v3-warn-text",
        className,
      )}
      data-testid="identifier-notice"
    >
      <ShieldAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>{t("visit.workspace.sources.shortNotice")}</span>
    </p>
  );
};

// File and screenshot upload for the workspace assistant: labs,
// investigations and previous notes become visit sources that the assistant
// and new notes can use. Only the extracted text is kept.
//
// Wraps the assistant body: drop a file anywhere on it, or paste a
// screenshot. The paperclip (AttachSourceButton) sits in the instruction box
// and attached files show as chips (SourceChips) under it.
export const AssistantSources = ({
  children,
  sources,
  loading = false,
  failed = false,
  onRetryList,
  onUpload,
  onRemove,
  isIncluded,
  onIncludedChange,
  prepare = prepareSourceFile,
  fast = AppConfig.enableAssistantFastUploads,
  onFileNamed,
  undoneIds,
  fileActions,
  className,
}: Props & { children: ReactNode; className?: string }): JSX.Element => {
  const { t } = useTranslation();
  const root = useRef<HTMLElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // Kept across reloads (useSourceNamesStore).
  const names = useSourceNames();
  const [warningFor, setWarningFor] = useState<File[] | null>(null);
  const [checked, setChecked] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<VisitSource | null>(null);
  const [removeError, setRemoveError] = useState(false);
  // Without fast attach one file is read at a time and blocks the next.
  const busy = !fast && pending.some((item) => ACTIVE.has(item.phase));

  const previews = useRef(new Map<string, string>());
  useEffect(
    () => () => {
      for (const url of previews.current.values()) URL.revokeObjectURL(url);
    },
    [],
  );
  const forget = (key: string): void => {
    const url = previews.current.get(key);
    if (url) URL.revokeObjectURL(url);
    previews.current.delete(key);
  };

  const problemText = (reason: SourceFileError): string =>
    t(
      fast && reason === "unsupported"
        ? "visit.workspace.sources.fileError.unsupportedFast"
        : `visit.workspace.sources.fileError.${reason}`,
    );

  const update = (key: string, change: Partial<Pending>): void => {
    setPending((items) =>
      items.map((item) => (item.key === key ? { ...item, ...change } : item)),
    );
  };
  const drop = (key: string): void => {
    forget(key);
    setPending((items) => items.filter((item) => item.key !== key));
  };

  const uploadError = (error: unknown): string => {
    const code =
      error && typeof error === "object" && "code" in error
        ? String(error.code)
        : "";
    const message =
      error instanceof Error && code === "BAD_REQUEST" ? error.message : "";
    return code === "SERVICE_UNAVAILABLE"
      ? t("visit.workspace.sources.unavailable")
      : message.startsWith("No readable") ||
          message.includes("too long") ||
          message.includes("maximum number") ||
          message.startsWith("Word documents")
        ? message
        : t(
            fast
              ? "visit.workspace.sources.attachFailed"
              : "visit.workspace.sources.uploadFailed",
          );
  };

  const read = async (current: Pending): Promise<void> => {
    update(current.key, {
      phase: fast ? "preparing" : "reading",
      error: undefined,
    });
    let input: SourceUpload;
    try {
      const prepared = await prepare(current.file, { docx: fast });
      input = {
        key: current.key,
        body: {
          timing: UPLOAD_TIMING,
          mediaType: prepared.mediaType,
          data: prepared.data,
          checksumSha256: prepared.checksumSha256,
          identifiersAcknowledged: true,
        },
      };
    } catch (error) {
      update(current.key, {
        phase: "error",
        error: problemText(
          error instanceof SourceFileProblem ? error.reason : "unsupported",
        ),
      });
      return;
    }
    if (fast) update(current.key, { phase: "uploading" });
    try {
      const created = await onUpload(input);
      const id =
        created && typeof created === "object" && "id" in created
          ? Number(created.id)
          : null;
      const cancelled = pendingRef.current.find(
        (item) => item.key === current.key,
      )?.cancelled;
      if (cancelled && id !== null) void onRemove(id).catch(() => {});
      else if (id !== null) {
        useSourceNamesStore.getState().setName(id, current.file.name);
        onFileNamed?.(id, current.file.name);
        // The local preview stays, now under the source (revoked when the
        // source is removed).
        const kind = sourceKindOf(current.file, { docx: true });
        const latest = pendingRef.current.find(
          (item) => item.key === current.key,
        );
        useSourcePreviews.getState().set(id, {
          url: previews.current.get(current.key) ?? null,
          kind:
            kind === "image" ? "image" : kind === "pdf" ? "pdf" : "document",
          pages: latest?.pages ?? null,
        });
        previews.current.delete(current.key);
      }
      drop(current.key);
    } catch (error) {
      if (
        pendingRef.current.find((item) => item.key === current.key)?.cancelled
      )
        drop(current.key);
      else update(current.key, { phase: "error", error: uploadError(error) });
    }
  };

  // Fast attach: start queued files, a few at a time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read is recreated each render; the queue is the trigger.
  useEffect(() => {
    if (!fast) return;
    const active = pending.filter((item) => ACTIVE.has(item.phase)).length;
    for (const item of pending
      .filter((entry) => entry.phase === "queued")
      .slice(0, Math.max(0, PARALLEL_UPLOADS - active)))
      void read(item);
  }, [fast, pending]);

  const queue = (files: File[]): void => {
    const items = files.map((file): Pending => {
      const key = crypto.randomUUID();
      let preview: string | null = null;
      const kind = sourceKindOf(file);
      // Images and PDFs: a local URL at once, for the thumbnail and the
      // large preview while the file is still on its way.
      if (
        (kind === "image" || kind === "pdf") &&
        typeof URL.createObjectURL === "function"
      ) {
        preview = URL.createObjectURL(file);
        previews.current.set(key, preview);
      }
      if (kind === "pdf")
        void pdfPageCount(file).then((pages) => {
          if (pages) update(key, { pages });
        });
      return { file, preview, key, phase: fast ? "queued" : "reading" };
    });
    setPending((current) => [...current, ...items]);
    if (!fast && items[0]) void read(items[0]);
  };

  // Picking, dropping or pasting files attaches them straight away.
  const start = (picked: File[]): void => {
    if (busy || !picked.length) return;
    setPickError(null);
    let files = fast ? picked : picked.slice(0, 1);
    if (fast) {
      // Every file that fails the quick checks shows as its own chip.
      const rejected = files.filter((file) =>
        checkSourceFile(file, { docx: true }),
      );
      files = files.filter((file) => !checkSourceFile(file, { docx: true }));
      const room = Math.max(
        0,
        VISIT_SOURCE_MAX_PER_VISIT -
          sources.length -
          pendingRef.current.filter((item) => item.phase !== "error").length,
      );
      if (files.length > room) {
        setPickError(
          room === 0
            ? t("visit.workspace.sources.full", {
                count: VISIT_SOURCE_MAX_PER_VISIT,
              })
            : t("visit.workspace.sources.tooMany", { count: room }),
        );
        files = files.slice(0, room);
      }
      if (rejected.length)
        setPending((current) => [
          ...current,
          ...rejected.map(
            (file): Pending => ({
              file,
              preview: null,
              key: crypto.randomUUID(),
              phase: "error",
              refused: true,
              error: problemText(
                checkSourceFile(file, { docx: true }) ?? "unsupported",
              ),
            }),
          ),
        ]);
      if (!files.length) return;
    } else {
      const problem = files[0] ? checkSourceFile(files[0]) : null;
      if (problem) {
        setPickError(problemText(problem));
        return;
      }
    }
    if (!readAck()) {
      setChecked(false);
      setWarningFor(files);
      return;
    }
    queue(files);
  };
  const startRef = useRef(start);
  startRef.current = start;

  // Ctrl/Cmd+V of a screenshot anywhere in the assistant (its Ask bar and
  // file tray), or on the page when nothing editable has focus. Never in the
  // note editor or another field outside it. The image goes straight from
  // the clipboard, named "Pasted image 14:32.png".
  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      // Another Ask bar of the visit (the dock and the panel) took it.
      if (event.defaultPrevented) return;
      const files = [...(event.clipboardData?.files ?? [])].filter((item) =>
        item.type.startsWith("image/"),
      );
      if (!files.length) return;
      const panel = root.current?.closest("[data-assistant-root]");
      const target = event.target instanceof Node ? event.target : null;
      const inside =
        !!target &&
        (!!root.current?.contains(target) || !!panel?.contains(target));
      if (isEditable(event.target)) {
        if (!inside) return;
        // Text copied with a picture (from a document or a web page): in a
        // field, the text is what is pasted.
        if (event.clipboardData?.getData?.("text/plain")?.trim()) return;
      }
      event.preventDefault();
      startRef.current(pastedImages(files, new Date()));
    };
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  const onFiles = (files: FileList | null): void => {
    start([...(files ?? [])]);
  };

  const removeNow = (source: VisitSource): void => {
    setRemoveError(false);
    onRemove(source.id)
      .then(() => {
        useSourcePreviews.getState().drop(source.id);
        useSourceNamesStore.getState().removeName(source.id);
      })
      .catch(() => {
        setRemoveError(true);
      });
  };
  const context: Context = {
    removeNow,
    undoneIds,
    fileActions,
    sources,
    loading,
    failed,
    onRetryList,
    isIncluded,
    onIncludedChange,
    fast,
    busy,
    dragging,
    pending,
    names,
    pickError,
    removeError,
    pickFile: () => fileInput.current?.click(),
    takePhoto: () => cameraInput.current?.click(),
    retry: (key) => {
      const item = pending.find((entry) => entry.key === key);
      if (!item || busy) return;
      if (fast) update(key, { phase: "queued", error: undefined });
      else void read(item);
    },
    dismissPending: (key) => {
      const item = pending.find((entry) => entry.key === key);
      if (item && item.phase === "uploading") update(key, { cancelled: true });
      else drop(key);
    },
    askRemove: (source) => {
      setRemoveError(false);
      setRemoving(source);
    },
  };

  return (
    <SourcesContext.Provider value={context}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target only; the paperclip button is the accessible way in. */}
      <section
        ref={root}
        aria-label={t("visit.workspace.sources.title")}
        className={cn("relative", className)}
        data-testid="assistant-sources"
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setDragging(false);
        }}
        onDragOver={(event) => {
          if (busy || !event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          onFiles(event.dataTransfer.files);
        }}
      >
        {children}
        {dragging && (
          <div
            className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-v3-brand bg-v3-panel/90 p-6 text-center backdrop-blur-[2px]"
            data-testid="source-drop-overlay"
          >
            <Paperclip aria-hidden className="size-6 text-v3-brand" />
            <p className="text-sm font-semibold text-v3-text">
              {t(
                fast
                  ? "visit.workspace.sources.dropHereMany"
                  : "visit.workspace.sources.dropHere",
              )}
            </p>
            <IdentifierNotice className="max-w-64 justify-center" />
          </div>
        )}
        <input
          ref={fileInput}
          accept={fast ? SOURCE_ACCEPT_FAST : SOURCE_ACCEPT}
          aria-label={t("visit.workspace.sources.attach")}
          className="sr-only"
          data-testid="source-file-input"
          multiple={fast}
          tabIndex={-1}
          type="file"
          onChange={(event) => {
            onFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={cameraInput}
          accept="image/*"
          aria-label={t("visit.workspace.sources.takePhoto")}
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          type="file"
          onChange={(event) => {
            onFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </section>

      <AlertDialog
        open={!!warningFor}
        onOpenChange={(open) => {
          if (!open) setWarningFor(null);
        }}
      >
        <AlertDialogContent data-testid="identifier-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center justify-center gap-2 sm:justify-start">
              <ShieldAlert aria-hidden className="size-5 text-v3-warn" />
              {t("visit.workspace.sources.warningTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("visit.workspace.sources.warningBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm font-normal leading-snug">
            <Checkbox
              checked={checked}
              className="mt-0.5 size-5 min-h-0!"
              onCheckedChange={(value) => {
                setChecked(value === true);
              }}
            />
            {t("visit.workspace.sources.warningConfirm")}
          </Label>
          <AlertDialogFooter className="gap-2">
            <Button
              className="h-11 sm:h-9"
              type="button"
              variant="outline"
              onClick={() => {
                setWarningFor(null);
              }}
            >
              {t("visit.workspace.sources.cancel")}
            </Button>
            <Button
              className="h-11 sm:h-9"
              disabled={!checked}
              type="button"
              onClick={() => {
                const files = warningFor;
                writeAck();
                setWarningFor(null);
                if (files) queue(files);
              }}
            >
              {t("visit.workspace.sources.continue")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog
        confirmText={t("visit.workspace.sources.removeConfirm")}
        description={t("visit.workspace.sources.removeBody")}
        open={!!removing}
        title={t("visit.workspace.sources.removeTitle")}
        onConfirm={() => {
          const source = removing;
          if (!source) return;
          removeNow(source);
        }}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      />
    </SourcesContext.Provider>
  );
};

// The paperclip in the instruction box (and a camera button on phones and
// tablets). Renders nothing outside AssistantSources.
export const AttachSourceButton = ({
  className,
  showLabel = false,
  camera = true,
}: {
  className?: string;
  // The camera button on phones and tablets (the Ask bar keeps it in the
  // paperclip's file picker, which offers the camera too).
  camera?: boolean;
  // A labelled outline button, for when there is no instruction box.
  showLabel?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const context = useContext(SourcesContext);
  if (!context) return null;
  const label = t(
    context.fast
      ? "visit.workspace.sources.attachHintFast"
      : "visit.workspace.sources.attachHint",
  );
  return (
    <span className={cn("flex items-center gap-1", className)}>
      <Button
        aria-label={label}
        className={
          showLabel
            ? "h-11 gap-1.5 lg:h-9"
            : "size-11 rounded-full text-muted-foreground hover:text-foreground lg:size-9"
        }
        data-testid="source-attach"
        disabled={context.busy}
        size={showLabel ? "default" : "icon"}
        title={`${label}\n${t("visit.workspace.sources.shortNotice")}`}
        type="button"
        variant={showLabel ? "outline" : "ghost"}
        onClick={context.pickFile}
      >
        <Paperclip />
        {showLabel && t("visit.workspace.sources.attach")}
      </Button>
      {camera && (
        <Button
          aria-label={t("visit.workspace.sources.takePhoto")}
          className="size-11 rounded-full text-muted-foreground hover:text-foreground lg:hidden"
          disabled={context.busy}
          size="icon"
          title={t("visit.workspace.sources.takePhoto")}
          type="button"
          variant="ghost"
          onClick={context.takePhoto}
        >
          <Camera />
        </Button>
      )}
    </span>
  );
};

const CHIP =
  "inline-flex h-9 max-w-full items-center lg:h-8 rounded-full border border-v3-border-strong bg-v3-panel text-xs text-v3-text";

const KIND_LABEL = {
  pdf: "visit.workspace.sources.pdf",
  image: "visit.workspace.sources.image",
  docx: "visit.workspace.sources.docx",
} as const satisfies Record<SourceFileKind, string>;

const PHASE_LABEL = {
  queued: "visit.workspace.sources.queued",
  preparing: "visit.workspace.sources.preparing",
  uploading: "visit.workspace.sources.attaching",
  reading: "visit.workspace.sources.reading",
  error: "visit.workspace.sources.attachFailed",
} as const satisfies Record<Phase, string>;

// A file on its way: thumbnail or icon, its name (fast attach) or type,
// a thin progress bar, then retry or X.
const PendingChip = ({
  item,
  fast,
  onRetry,
  onDismiss,
}: {
  item: Pending;
  fast: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const kind = sourceKindOf(item.file, { docx: fast }) ?? "image";
  const failed = item.phase === "error";
  const working = !failed && item.phase !== "queued";
  const label = fast ? item.file.name : t(KIND_LABEL[kind]);
  return (
    <li
      aria-busy={working}
      className={cn(
        CHIP,
        "relative gap-1.5 overflow-hidden pl-1 pr-1",
        failed && "border-destructive/50",
        item.cancelled && "opacity-60",
      )}
      data-phase={item.phase}
      data-testid="pending-source"
      title={failed ? item.error : t(PHASE_LABEL[item.phase])}
    >
      {item.preview ? (
        <img
          alt=""
          className="size-6 shrink-0 rounded-full border object-cover"
          src={item.preview}
        />
      ) : (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-v3-surface-2">
          <FileText aria-hidden className="size-3.5" />
        </span>
      )}
      <span className="min-w-0 truncate">
        <span
          className={cn(fast && "inline-block max-w-36 truncate align-bottom")}
        >
          {label}
        </span>
        <span className="text-v3-text-3">
          {" · "}
          {fast && !failed
            ? t(PHASE_LABEL[item.phase])
            : formatBytes(item.file.size)}
        </span>
      </span>
      {!fast && item.phase === "reading" && (
        <Loader2
          aria-hidden
          className="mr-1 size-3.5 shrink-0 animate-spin text-v3-brand"
        />
      )}
      {failed && !item.refused && (
        <button
          aria-label={t("visit.workspace.sources.retry")}
          className="flex size-6 shrink-0 items-center justify-center rounded-full text-v3-brand hover:bg-v3-brand-tint"
          title={t("visit.workspace.sources.retry")}
          type="button"
          onClick={onRetry}
        >
          <RotateCw aria-hidden className="size-3.5" />
        </button>
      )}
      {(fast || failed) && (
        <button
          aria-label={
            fast
              ? t("visit.workspace.sources.removePending", {
                  name: item.file.name,
                })
              : t("visit.workspace.sources.cancel")
          }
          className="flex size-6 shrink-0 items-center justify-center rounded-full text-v3-text-2 hover:bg-v3-surface-2"
          title={t("visit.workspace.sources.cancel")}
          type="button"
          onClick={onDismiss}
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
      {fast && working && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-v3-brand-tint"
        >
          <span
            className={cn(
              "block h-full bg-v3-brand transition-[width] duration-500",
              item.phase === "preparing" ? "w-1/3" : "w-2/3 animate-pulse",
            )}
          />
        </span>
      )}
    </li>
  );
};

// Attached files as small chips under the instruction box: the ones on
// their way, then the visit's sources. A chip opens its details (date,
// extracted text, "Use in notes"); the x removes it.
export const SourceChips = ({
  className,
  thumbnails = false,
}: {
  className?: string;
  // v3.2: the files as a row of small previews (FileThumb) that scrolls
  // sideways, one chip high; a preview opens the file larger.
  thumbnails?: boolean;
}): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const context = useContext(SourcesContext);
  if (!context) return null;
  const { sources, pending, pickError, removeError, failed, loading, fast } =
    context;
  if (
    !sources.length &&
    !pending.length &&
    !pickError &&
    !removeError &&
    !failed &&
    !loading
  )
    return null;
  const inUse = sources.filter((s) => context.isIncluded(s.id)).length;
  const working = pending.filter((item) => ACTIVE.has(item.phase));
  const errors = pending.filter((item) => item.phase === "error");

  return (
    <div className={cn("space-y-1.5", className)} data-testid="source-chips">
      {thumbnails && (sources.length > 0 || pending.length > 0) && (
        <SourceThumbTray context={context} />
      )}
      {!thumbnails && (sources.length > 0 || pending.length > 0) && (
        <ul
          aria-label={t("visit.workspace.sources.title")}
          className="flex flex-wrap gap-1.5"
          data-testid="source-list"
        >
          {pending.map((item) => (
            <PendingChip
              key={item.key}
              fast={fast}
              item={item}
              onDismiss={() => {
                context.dismissPending(item.key);
              }}
              onRetry={() => {
                context.retry(item.key);
              }}
            />
          ))}
          {sources.map((source) => (
            <SourceChip
              key={source.id}
              included={context.isIncluded(source.id)}
              language={i18n?.language ?? "en"}
              name={context.names.get(source.id)}
              source={source}
              onIncludedChange={(value) => {
                context.onIncludedChange(source.id, value);
              }}
              onRemove={() => {
                context.askRemove(source);
              }}
            />
          ))}
        </ul>
      )}
      {working.length > 0 && (
        <p className="sr-only" role="status">
          {fast
            ? t("visit.workspace.sources.attachingCount", {
                count: working.length,
              })
            : t("visit.workspace.sources.reading")}
        </p>
      )}
      {pending.length > 0 && <IdentifierNotice />}
      {errors.map(
        (item) =>
          item.error && (
            <p
              key={item.key}
              className="text-xs text-destructive wrap-anywhere"
              role="alert"
            >
              {fast ? `${item.file.name}: ${item.error}` : item.error}
            </p>
          ),
      )}
      {pickError && (
        <p className="text-xs text-destructive" role="alert">
          {pickError}
        </p>
      )}
      {removeError && (
        <p className="text-xs text-destructive" role="alert">
          {t("visit.workspace.sources.removeFailed")}
        </p>
      )}
      {failed && (
        <p
          className="flex items-center gap-2 text-xs text-destructive"
          role="alert"
        >
          {t("visit.workspace.sources.listFailed")}
          <button
            className="font-medium text-v3-brand underline-offset-2 hover:underline"
            type="button"
            onClick={context.onRetryList}
          >
            {t("visit.workspace.sources.retry")}
          </button>
        </p>
      )}
      {loading && !sources.length && (
        <p className="text-xs text-muted-foreground" role="status">
          {t("visit.workspace.sources.loading")}
        </p>
      )}
      {/* The previews mark each file in use; the count line is for chips. */}
      {!thumbnails && sources.length > 1 && (
        <p className="text-xs text-muted-foreground">
          {t("visit.workspace.sources.inUse", {
            count: inUse,
            total: sources.length,
          })}
        </p>
      )}
    </div>
  );
};

const SourceChip = ({
  source,
  name,
  included,
  language,
  onIncludedChange,
  onRemove,
}: {
  source: VisitSource;
  // The picked file's name, in the session that attached it.
  name?: string;
  included: boolean;
  language: string;
  onIncludedChange: (included: boolean) => void;
  onRemove: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const Icon = KIND_ICON[source.kind];
  const title = name ?? source.title;
  const date = source.documentDate
    ? formatDate(source.documentDate, language)
    : t("visit.workspace.sources.noDate");
  return (
    <li
      className={cn(
        CHIP,
        "pr-0.5",
        !included && "border-dashed text-v3-text-2",
      )}
      data-included={included}
      data-read-on-use={source.readOnUse === true}
      data-testid="source-item"
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            aria-label={t("visit.workspace.sources.details", { title })}
            className="flex h-full min-w-0 items-center gap-1.5 rounded-l-full pl-2.5 pr-1 hover:text-v3-brand focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            type="button"
          >
            <Icon
              aria-hidden
              className={cn(
                "size-3.5 shrink-0",
                included ? "text-v3-brand" : "text-v3-text-3",
              )}
            />
            <span className="max-w-40 truncate font-medium">{title}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3"
          data-testid="source-details"
        >
          <div>
            <p className="wrap-break-word text-sm font-semibold leading-5">
              {title}
            </p>
            <p className="text-xs text-muted-foreground">
              {source.readOnUse && source.kind === "other"
                ? t(
                    source.mediaType.startsWith("image/")
                      ? "visit.workspace.sources.image"
                      : "visit.workspace.sources.pdf",
                  )
                : t(`visit.workspace.sources.kind.${source.kind}`)}
              {" · "}
              {date}
            </p>
          </div>
          {source.identifiersRedacted && (
            <p className="flex gap-1.5 text-xs text-muted-foreground">
              <ShieldAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
              {t("visit.workspace.sources.redacted")}
            </p>
          )}
          {source.readOnUse && (
            <p
              className="flex gap-1.5 rounded-md bg-v3-brand-tint px-2.5 py-2 text-xs leading-5 text-v3-text"
              data-testid="source-read-on-use"
            >
              <Paperclip
                aria-hidden
                className="mt-0.5 size-3.5 shrink-0 text-v3-brand"
              />
              {t("visit.workspace.sources.readOnUse")}
            </p>
          )}
          {source.text && (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap wrap-break-word rounded-md bg-muted/60 p-2.5 font-sans text-xs leading-5 wrap-anywhere">
              {source.text}
            </pre>
          )}
          <div className="flex items-center justify-between gap-2">
            <Label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-normal lg:min-h-9">
              <Checkbox
                checked={included}
                className="size-5 min-h-0!"
                onCheckedChange={(value) => {
                  onIncludedChange(value === true);
                }}
              />
              {t("visit.workspace.sources.use")}
            </Label>
            <Button
              className="h-11 gap-1.5 px-2 text-destructive lg:h-9"
              type="button"
              variant="ghost"
              onClick={onRemove}
            >
              <Trash2 aria-hidden />
              {t("visit.workspace.sources.removeConfirm")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        aria-label={t("visit.workspace.sources.remove", { title })}
        className="flex size-8 shrink-0 items-center justify-center rounded-full text-v3-text-3 lg:size-7 hover:bg-v3-surface-2 hover:text-v3-text"
        title={t("visit.workspace.sources.remove", { title })}
        type="button"
        onClick={onRemove}
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </li>
  );
};

// The files as small previews in one row (v3.2): the ones on their way
// (from the picked file at once, with a spinner), then the visit's sources
// (in use, not used, or undone). A preview opens the file larger, with
// Remove; an undone file can be added again from there.
type Opened = { type: "pending"; key: string } | { type: "source"; id: number };
const SourceThumbTray = ({ context }: { context: Context }): JSX.Element => {
  const { t, i18n } = useTranslation();
  const previews = useSourcePreviews((state) => state.byId);
  const [opened, setOpened] = useState<Opened | null>(null);
  const labels = {
    uploading: t("visit.workspace.sources.thumb.uploading"),
    inUse: t("visit.workspace.sources.thumb.inUse"),
    undone: t("visit.workspace.sources.thumb.undone"),
    error: t("visit.workspace.sources.thumb.error"),
    pages: (count: number) =>
      t("visit.workspace.sources.thumb.pages", { count }),
  };
  const pendingKind = (file: File): FileThumbKind => {
    const kind = sourceKindOf(file, { docx: true });
    return kind === "image" ? "image" : kind === "pdf" ? "pdf" : "document";
  };
  const sourceKind = (source: VisitSource): FileThumbKind =>
    previews[source.id]?.kind ??
    (source.mediaType.startsWith("image/")
      ? "image"
      : source.mediaType === "application/pdf"
        ? "pdf"
        : "document");
  const stateOf = (source: VisitSource): FileThumbState =>
    context.undoneIds?.has(source.id)
      ? "undone"
      : context.isIncluded(source.id)
        ? "in-use"
        : "ready";
  const openPending = context.pending.find(
    (item) => opened?.type === "pending" && item.key === opened.key,
  );
  const openSource = context.sources.find(
    (source) => opened?.type === "source" && source.id === opened.id,
  );
  return (
    <>
      <FileTray
        allTitle={t("visit.workspace.sources.title")}
        aria-label={t("visit.workspace.sources.title")}
        closeLabel={t("visit.workspace.sources.thumb.close")}
        data-testid="source-list"
        moreLabel={(count) =>
          t("visit.workspace.sources.thumb.more", { count })
        }
      >
        {context.pending.map((item) => (
          <li
            key={item.key}
            data-phase={item.phase}
            data-testid="pending-source"
          >
            <FileThumb
              kind={pendingKind(item.file)}
              labels={labels}
              name={item.file.name}
              pages={item.pages ?? null}
              src={item.preview}
              state={item.phase === "error" ? "error" : "uploading"}
              onClick={() => setOpened({ type: "pending", key: item.key })}
            />
          </li>
        ))}
        {context.sources.map((source) => {
          const preview = previews[source.id];
          return (
            <li
              key={source.id}
              data-included={context.isIncluded(source.id)}
              data-testid="source-item"
              data-undone={context.undoneIds?.has(source.id) || undefined}
            >
              <FileThumb
                kind={sourceKind(source)}
                labels={labels}
                name={context.names.get(source.id) ?? source.title}
                pages={preview?.pages ?? null}
                src={preview?.url ?? null}
                state={stateOf(source)}
                onClick={() => setOpened({ type: "source", id: source.id })}
              />
            </li>
          );
        })}
      </FileTray>
      <Sheet
        closeLabel={t("visit.workspace.sources.thumb.close")}
        footer={
          openSource ? (
            // Inside the sheet's 20px side padding, as the body.
            <div
              className="flex flex-wrap items-center justify-end gap-2 px-2"
              data-testid="source-preview-actions"
            >
              {context.fileActions?.(openSource, () => setOpened(null))}
              <V32Button
                className={context.fileActions ? "max-sm:w-full" : undefined}
                data-testid="source-preview-remove"
                variant="destructive"
                onClick={() => {
                  context.removeNow(openSource);
                  setOpened(null);
                }}
              >
                <Trash2 aria-hidden />
                {t("visit.workspace.sources.removeConfirm")}
              </V32Button>
            </div>
          ) : openPending ? (
            <div className="flex justify-end px-2">
              <V32Button
                variant="destructive"
                onClick={() => {
                  context.dismissPending(openPending.key);
                  setOpened(null);
                }}
              >
                <X aria-hidden />
                {t("visit.workspace.sources.cancel")}
              </V32Button>
            </div>
          ) : undefined
        }
        open={!!(openSource || openPending)}
        testId="source-preview"
        title={
          <span className="block truncate">
            {openSource
              ? (context.names.get(openSource.id) ?? openSource.title)
              : (openPending?.file.name ?? "")}
          </span>
        }
        onOpenChange={(open) => {
          if (!open) setOpened(null);
        }}
      >
        {openSource || openPending ? (
          <SourcePreviewBody
            context={context}
            language={i18n?.language ?? "en"}
            pending={openPending}
            preview={openSource ? previews[openSource.id] : undefined}
            source={openSource}
          />
        ) : (
          <span />
        )}
      </Sheet>
    </>
  );
};

const SourcePreviewBody = ({
  context,
  source,
  pending,
  preview,
  language,
}: {
  context: Context;
  source?: VisitSource;
  pending?: Pending;
  preview?: SourcePreview;
  language: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const url = source ? (preview?.url ?? null) : (pending?.preview ?? null);
  const kind = source
    ? (preview?.kind ??
      (source.mediaType.startsWith("image/") ? "image" : "pdf"))
    : sourceKindOf(pending?.file as File) === "image"
      ? "image"
      : "pdf";
  const pages = source ? preview?.pages : pending?.pages;
  return (
    <div
      className="space-y-3 px-5 pb-3 font-v3"
      data-testid="source-preview-body"
    >
      <div className="flex min-h-48 items-center justify-center overflow-hidden rounded-[14px] bg-v3-surface-2 shadow-v3-hairline">
        {url && kind === "image" ? (
          <img
            alt=""
            className="max-h-[55dvh] w-full object-contain"
            src={url}
          />
        ) : url && kind === "pdf" ? (
          <object
            aria-label={t("visit.workspace.sources.pdf")}
            className="h-[55dvh] w-full"
            data={url}
            type="application/pdf"
          >
            <FileText aria-hidden className="size-10 text-v3-text-3" />
          </object>
        ) : (
          <span className="flex flex-col items-center gap-2 py-10 text-v3-text-2">
            <FileText aria-hidden className="size-10" strokeWidth={1.5} />
            <span className="text-[13px]">
              {t("visit.workspace.sources.thumb.noPreview")}
            </span>
          </span>
        )}
      </div>
      <p className="text-[13px] text-v3-text-2">
        {[
          kind === "image"
            ? t("visit.workspace.sources.image")
            : t("visit.workspace.sources.pdf"),
          pages
            ? t("visit.workspace.sources.thumb.pages", { count: pages })
            : null,
          source?.documentDate
            ? formatDate(source.documentDate, language)
            : null,
          pending
            ? t(PHASE_LABEL[pending.phase])
            : source && context.undoneIds?.has(source.id)
              ? t("visit.workspace.sources.thumb.undone")
              : source && context.isIncluded(source.id)
                ? t("visit.workspace.sources.thumb.inUse")
                : t("visit.workspace.sources.thumb.notUsed"),
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {source && context.undoneIds?.has(source.id) && (
        <p
          className="rounded-[12px] bg-v3-surface-2 px-3 py-2 text-[13px] leading-5 text-v3-text-2"
          data-testid="source-undone-hint"
        >
          {t("visit.workspace.sources.thumb.undoneHint")}
        </p>
      )}
      {source && !context.undoneIds?.has(source.id) && (
        <Label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-normal lg:min-h-9">
          <Checkbox
            checked={context.isIncluded(source.id)}
            className="size-5 min-h-0!"
            onCheckedChange={(value) => {
              context.onIncludedChange(source.id, value === true);
            }}
          />
          {t("visit.workspace.sources.use")}
        </Label>
      )}
      {source?.text && (
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-[12px] bg-v3-surface-2 p-2.5 font-sans text-xs leading-5 wrap-anywhere">
          {source.text}
        </pre>
      )}
    </div>
  );
};

// Files still being attached (fast attach): the Ask bar waits for them, so
// a question never goes out without a file the clinician just added.
export const useSourcesAttaching = (): boolean => {
  const context = useContext(SourcesContext);
  return !!context?.pending.some(
    (item) => item.phase === "queued" || ACTIVE.has(item.phase),
  );
};
